/** Date stylisée — même geste que l’agenda MCV, charte FacturX. */
import { useEffect, useMemo, useRef, useState } from "react";
import { MONTHS_FR, formatDay } from "../formats/display";
import "./fxCtrl.css";

const WEEK = ["L", "M", "M", "J", "V", "S", "D"];

type FxDateProps = {
  id?: string;
  value: string;
  disabled?: boolean;
  min?: string;
  max?: string;
  onChange: (iso: string) => void;
};

function toIso(d: Date): string {
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${m}-${day}`;
}

function todayIso(): string {
  return toIso(new Date());
}

function parseIso(iso: string): Date | null {
  const [y, m, d] = iso.split("-").map(Number);
  if (!y || !m || !d) return null;
  const dt = new Date(y, m - 1, d);
  return Number.isNaN(dt.getTime()) ? null : dt;
}

function cells(year: number, month: number) {
  const first = new Date(year, month, 1);
  const pad = (first.getDay() + 6) % 7;
  const out: { iso: string; day: number; other: boolean }[] = [];
  for (let i = 0; i < pad; i += 1) {
    const d = new Date(year, month, i - pad + 1);
    out.push({ iso: toIso(d), day: d.getDate(), other: true });
  }
  const last = new Date(year, month + 1, 0).getDate();
  for (let d = 1; d <= last; d += 1) {
    out.push({ iso: toIso(new Date(year, month, d)), day: d, other: false });
  }
  while (out.length % 7 !== 0) {
    const d = new Date(year, month + 1, out.length - pad - last + 1);
    out.push({ iso: toIso(d), day: d.getDate(), other: true });
  }
  return out;
}

function outOfRange(iso: string, min?: string, max?: string): boolean {
  return Boolean((min && iso < min) || (max && iso > max));
}

export function FxDate({ id, value, disabled, min, max, onChange }: FxDateProps) {
  const wrap = useRef<HTMLDivElement>(null);
  const btn = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const selected = parseIso(value);
  const [cursor, setCursor] = useState(() => selected ?? new Date());

  function place() {
    const r = btn.current?.getBoundingClientRect();
    if (!r) return;
    setPos({ top: r.bottom + 6, left: r.left });
  }

  useEffect(() => {
    if (open) {
      setCursor(selected ?? new Date());
      place();
    }
  }, [open, value]);

  useEffect(() => {
    if (!open) return;
    function onDoc(e: MouseEvent) {
      if (!wrap.current?.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [open]);

  const days = useMemo(() => cells(cursor.getFullYear(), cursor.getMonth()), [cursor]);
  const today = todayIso();

  function pick(iso: string) {
    if (outOfRange(iso, min, max)) return;
    onChange(iso);
    setOpen(false);
  }

  return (
    <div ref={wrap} className={open ? "fx-ctrl fx-date is-open" : "fx-ctrl fx-date"}>
      <button
        ref={btn}
        id={id}
        type="button"
        className="fx-ctrl__btn"
        disabled={disabled}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => !disabled && setOpen((v) => !v)}
      >
        <span className="fx-ctrl__ico" aria-hidden>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
            <rect x="4" y="5" width="16" height="15" rx="2" />
            <path d="M8 3v4M16 3v4M4 10h16" />
          </svg>
        </span>
        <span className={value ? "fx-ctrl__lab" : "fx-ctrl__lab is-empty"}>{value ? formatDay(value) : "Choisir"}</span>
        <span className="fx-ctrl__chev" aria-hidden>
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M6 9l6 6 6-6" />
          </svg>
        </span>
      </button>
      {open ? (
        <div className="fx-ctrl__panel fx-date__panel" role="dialog" style={{ top: pos.top, left: pos.left }}>
          <div className="fx-date__nav">
            <button type="button" aria-label="Mois précédent" onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))}>
              ‹
            </button>
            <strong>
              {MONTHS_FR[cursor.getMonth()]} {cursor.getFullYear()}
            </strong>
            <button type="button" aria-label="Mois suivant" onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))}>
              ›
            </button>
          </div>
          <div className="fx-date__week">
            {WEEK.map((w, i) => (
              <span key={`${w}-${i}`}>{w}</span>
            ))}
          </div>
          <div className="fx-date__grid">
            {days.map((d) => {
              const off = outOfRange(d.iso, min, max);
              return (
                <button
                  key={d.iso}
                  type="button"
                  disabled={off}
                  className={[d.other ? "is-other" : "", off ? "is-off" : "", d.iso === today ? "is-today" : "", d.iso === value ? "is-on" : ""]
                    .filter(Boolean)
                    .join(" ")}
                  onClick={() => pick(d.iso)}
                >
                  {d.day}
                </button>
              );
            })}
          </div>
          <button type="button" className="fx-date__today" disabled={outOfRange(today, min, max)} onClick={() => pick(today)}>
            Aujourd’hui
          </button>
        </div>
      ) : null}
    </div>
  );
}
